// FAANG-GRADE: Centralized Dashboard Routing Utility
// Provides consistent role-based dashboard URL resolution across the application

export type UserRole = 'customer' | 'partner_admin' | 'partner_staff' | 'admin' | 'super_admin';

/**
 * Get the correct dashboard URL for a given user role
 * @param role - The user's role
 * @returns The appropriate dashboard URL for the role
 */
export function getDashboardUrlForRole(role: UserRole | string): string {
  // Customer users default to marketplace, others use dashboard
  if (role === 'customer') {
    return '/';
  }
  // All other roles use the /dashboard route - RoleBasedPage handles component selection
  return '/dashboard';
}

/**
 * Get the dashboard URL for the current user from localStorage
 * @returns The appropriate dashboard URL for the current user
 */
export function getCurrentUserDashboardUrl(): string {
  if (typeof window === 'undefined') return '/dashboard/customer';

  try {
    const userJson = localStorage.getItem('user');
    console.log('[DashboardRouting] localStorage user JSON:', userJson);
    const user = JSON.parse(userJson || 'null');
    console.log('[DashboardRouting] Parsed user object:', user);
    const role = (user as any)?.role || 'customer';
    console.log('[DashboardRouting] User role:', role);
    const url = getDashboardUrlForRole(role);
    console.log('[DashboardRouting] Dashboard URL for role:', role, '->', url);
    return url;
  } catch (error) {
    console.warn('[DashboardRouting] Error getting current user dashboard URL:', error);
    return '/dashboard/customer';
  }
}

/**
 * Navigate to the appropriate dashboard for the current user
 * @param router - Next.js router instance
 */
export function navigateToUserDashboard(router: any): void {
  const dashboardUrl = getCurrentUserDashboardUrl();
  console.log(`[DashboardRouting] Navigating to user dashboard: ${dashboardUrl}`);
  router.replace(dashboardUrl);
}

/**
 * Check if a given URL is a role-specific dashboard URL
 * @param url - The URL to check
 * @returns True if the URL is a role-specific dashboard
 */
export function isRoleSpecificDashboardUrl(url: string): boolean {
  return url.startsWith('/dashboard/') && url !== '/dashboard';
}

/**
 * FAANG-GRADE: Generalized role-based page routing utilities
 * Extends dashboard routing pattern to other pages requiring role variants
 */

/**
 * Get the correct marketplace URL for a given user role
 * @param role - The user's role
 * @returns The appropriate marketplace URL for the role
 */
export function getMarketplaceUrlForRole(role: UserRole | string): string {
  // For now, all roles use the same marketplace URL
  // In Phase 2, this will route to role-specific variants
  return '/marketplace';
}

/**
 * Get the correct analytics URL for a given user role
 * @param role - The user's role
 * @returns The appropriate analytics URL for the role
 */
export function getAnalyticsUrlForRole(role: UserRole | string): string {
  // For now, all roles use the same analytics URL
  // In Phase 2, this will route to role-specific variants
  return '/analytics';
}

/**
 * Get the correct settings URL for a given user role
 * @param role - The user's role
 * @returns The appropriate settings URL for the role
 */
export function getSettingsUrlForRole(role: UserRole | string): string {
  // For now, all roles use the same settings URL
  // In Phase 2, this will route to role-specific variants
  return '/settings';
}

/**
 * Get the correct knowledge base URL for a given user role
 * @param role - The user's role
 * @returns The appropriate knowledge base URL for the role
 */
export function getKnowledgeBaseUrlForRole(role: UserRole | string): string {
  // For now, all roles use the same KB URL
  // In Phase 2, this will route to role-specific variants
  return '/kb';
}

/**
 * Get the current user's marketplace URL
 * @returns The appropriate marketplace URL for the current user
 */
export function getCurrentUserMarketplaceUrl(): string {
  if (typeof window === 'undefined') return '/marketplace';

  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const role = (user as any)?.role || 'customer';
    return getMarketplaceUrlForRole(role);
  } catch (error) {
    console.warn('[PageRouting] Error getting current user marketplace URL:', error);
    return '/';
  }
}

/**
 * Get the current user's analytics URL
 * @returns The appropriate analytics URL for the current user
 */
export function getCurrentUserAnalyticsUrl(): string {
  if (typeof window === 'undefined') return '/analytics';

  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const role = (user as any)?.role || 'customer';
    return getAnalyticsUrlForRole(role);
  } catch (error) {
    console.warn('[PageRouting] Error getting current user analytics URL:', error);
    return '/analytics';
  }
}

/**
 * Get the current user's settings URL
 * @returns The appropriate settings URL for the current user
 */
export function getCurrentUserSettingsUrl(): string {
  if (typeof window === 'undefined') return '/settings';

  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const role = (user as any)?.role || 'customer';
    return getSettingsUrlForRole(role);
  } catch (error) {
    console.warn('[PageRouting] Error getting current user settings URL:', error);
    return '/settings';
  }
}

/**
 * Get the current user's knowledge base URL
 * @returns The appropriate knowledge base URL for the current user
 */
export function getCurrentUserKnowledgeBaseUrl(): string {
  if (typeof window === 'undefined') return '/kb';

  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const role = (user as any)?.role || 'customer';
    return getKnowledgeBaseUrlForRole(role);
  } catch (error) {
    console.warn('[PageRouting] Error getting current user KB URL:', error);
    return '/kb';
  }
}